import React, { useCallback, useRef, useState, useLayoutEffect } from "react";
import ReactTooltip from "react-tooltip";
import { Heading5 } from "./GlobalElements/Typography";
import { tokens } from "../../public/tokens";

export const TrimText = (value, size = 40) => {
  return value !== undefined && value.length > size
    ? value.slice(0, size) + "..."
    : value;
};

export function DynamicTrimText({ 
  text, 
  showTooltip=false,
  trimText = true,
  as: Component = Heading5,
  children,
  ...props
}) {
  const ref = useRef(null);
  const [trimmed, setTrimmed] = useState(text);
  const [needsEllipsis, setNeedsEllipsis] = useState(false);

  const measure = useCallback(() => {
    if (!trimText) return;

    const el = ref.current;
    if (!el) return;

    const style = getComputedStyle(el);

    const font = [
      style.fontStyle,
      style.fontVariant,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
    ].join(" ");

    const canvas =
      measure.canvas || (measure.canvas = document.createElement("canvas"));

    const ctx = canvas.getContext("2d");
    ctx.font = font;

    const childWidth = Array.from(el.children)
      .filter((child) => !child.classList.contains("dynamic-trim-text__ellipsis"))
      .reduce((width, child) => width + child.getBoundingClientRect().width, 0);
    const avail = Math.max(el.clientWidth - childWidth, 0);
    const ell = "…";
    const ellW = ctx.measureText(ell).width;
    const fullW = ctx.measureText(text).width;

    if (fullW <= avail) {
      setTrimmed(text);
      setNeedsEllipsis(false);
      return;
    }

    let lo = 0, hi = text.length;

    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);

      if (ctx.measureText(text.slice(0, mid)).width + ellW <= avail) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    setTrimmed(text.slice(0, lo));
    setNeedsEllipsis(true);
  }, [text, trimText]);

  useLayoutEffect(measure, [measure]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, [measure]);

  return (
    !trimText ? (
      <Component
        ref={ref}
        title={props.title || text}
        className={`dynamic-trim-text dynamic-trim-text--no-trim ${props.className || ""}`}
        {...props}
      >
        {children}
        {text}
      </Component>
    ) : (
    <Component
      ref={ref}
      title={props.title || text}
      className={`dynamic-trim-text ${props.className || ""}`}
      {...props}
    >
      {children}
      {trimmed}
      {needsEllipsis && (
        <>
          <span
            className="dynamic-trim-text__ellipsis"
            data-tip={`<div class="dynamic-trim-text__tooltip"><p>${text}</p></div>`}
            data-for="title-tooltip"
          >
            …
          </span>

          {showTooltip?.toString().toLowerCase() === "true"  && (
            <ReactTooltip
              html
              id="title-tooltip"
              backgroundColor={tokens.colors.theme["button-color"]}
              effect="solid"
              padding="4px"
              place="left"
              className="title-tooltip-card"
              delayHide={200}
              delayShow={200}
              offset={{ bottom: 8, left: 2 }}
            />
          )}
        </>
          )}
    </Component>
    )
  );
}
